import { useState } from "react";
import { Card, CardContent, Skeleton, Button } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "@tellescope/utils";
import { User, ChevronRight, Plus } from "lucide-react";
import type { ChatRoom } from "@tellescope/types-models";
import type { ChatRoomListBlock as ChatRoomListBlockType } from "../../../shared/schema";
import { MobileList } from "../MobileList";
import { useEnduserChatRooms } from "../../hooks/useEnduserChatRooms";
import { useChatRoomsFilter } from "../../hooks/useItemFilter";
import { ChatDialog } from "./ChatDialog";
import { NewConversationDialog } from "./NewConversationDialog";

interface ChatRoomListBlockProps {
  block: ChatRoomListBlockType;
}

interface ChatRoomCardProps {
  chatRoom: ChatRoom;
  onClick: () => void;
}

function ChatRoomCard({ chatRoom, onClick }: ChatRoomCardProps) {
  const { t } = useTranslation("messages");
  // Determine display title - use title, topic, or fallback
  const displayTitle = chatRoom.title || chatRoom.topic || t("Conversation");

  // Get the timestamp to display
  const timestamp = chatRoom.recentMessageSentAt || chatRoom.updatedAt;

  return (
    <Card className="overflow-hidden cursor-pointer hover:bg-accent/50 transition-colors" onClick={onClick}>
      <CardContent className="px-4">
        <div className="flex gap-3 items-center">
          {/* Avatar/icon */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-5 w-5 text-primary" />
            </div>
          </div>

          {/* Chat details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h4 className="font-medium text-sm truncate">{displayTitle}</h4>
              <span className="text-xs text-muted-foreground flex-shrink-0">
                {formatDateTime(timestamp, "MMM D, YYYY at hh:mm a", { unit: "days", amount: 7, direction: "past" })}
              </span>
            </div>

            {chatRoom.recentMessage && (
              <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{chatRoom.recentMessage}</p>
            )}

            {chatRoom.numMessages > 0 && !chatRoom.recentMessage && (
              <p className="text-xs text-muted-foreground mt-1">
                {t("{{count}} message", { count: chatRoom.numMessages })}
              </p>
            )}
          </div>

          {/* Chevron indicator */}
          <div className="flex-shrink-0">
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChatRoomListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="px-4">
            <div className="flex gap-3">
              <Skeleton className="w-10 h-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-3 w-12" />
                </div>
                <Skeleton className="h-3 w-3/4" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function ChatRoomListBlock({ block }: ChatRoomListBlockProps) {
  const { t } = useTranslation("messages");
  const { itemFilter, allowNewConversation } = block.props;
  const [selectedChatRoom, setSelectedChatRoom] = useState<ChatRoom | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newConversationOpen, setNewConversationOpen] = useState(false);

  const { chatRooms: rawChatRooms, isLoading: chatRoomsLoading, error } = useEnduserChatRooms();

  // Apply item filter if configured
  const { filteredItems: chatRooms, isLoading: filterLoading } = useChatRoomsFilter({
    items: rawChatRooms,
    itemFilter,
  });

  const isLoading = chatRoomsLoading || filterLoading;

  const handleChatRoomClick = (chatRoom: ChatRoom) => {
    setSelectedChatRoom(chatRoom);
    setDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      // Clear selection after dialog closes (small delay for animation)
      setTimeout(() => setSelectedChatRoom(null), 200);
    }
  };

  const handleConversationCreated = () => {
    setNewConversationOpen(false);
  };

  if (isLoading) {
    return <ChatRoomListSkeleton count={block.props.maxItems ?? 5} />;
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="p-4 text-center text-destructive">{t("Failed to load messages")}</CardContent>
      </Card>
    );
  }

  return (
    <>
      {allowNewConversation && (
        <Button variant="outline" className="w-full mb-3" onClick={() => setNewConversationOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t("New Conversation")}
        </Button>
      )}

      <MobileList
        items={chatRooms}
        renderItem={(chatRoom) => <ChatRoomCard chatRoom={chatRoom} onClick={() => handleChatRoomClick(chatRoom)} />}
        title={t("Your Conversations")}
        maxItems={block.props.maxItems ?? 5}
        emptyMessage={block.props.emptyMessage ?? t("No messages")}
      />

      <ChatDialog chatRoom={selectedChatRoom} open={dialogOpen} onOpenChange={handleDialogClose} />

      {allowNewConversation && (
        <NewConversationDialog
          open={newConversationOpen}
          onOpenChange={setNewConversationOpen}
          onConversationCreated={handleConversationCreated}
        />
      )}
    </>
  );
}
